import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

export function ChatBar({
    Icon,
    value,
    onChange,
    onSend,
    placeholder = "Try using K-Quant to deeply analyze and interpret financial News",
    autoFocus,
    disabled,
}: {
    Icon: React.ComponentType<{ className?: string }>;
    value?: string;
    onChange?: (v: string) => void;
    onSend?: (text: string) => void;
    placeholder?: string;
    autoFocus?: boolean;
    disabled?: boolean;
}) {
    const isControlled = value !== undefined;
    const [innerValue, setInnerValue] = useState(value ?? "");
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const maxHeightPx = 120;

    useEffect(() => {
        if (!isControlled) return;
        setInnerValue(value ?? "");
    }, [isControlled, value]);

    const text = isControlled ? value ?? "" : innerValue;
    const canSend = useMemo(() => {
        if (disabled) return false;
        return (text ?? "").trim().length > 0;
    }, [disabled, text]);

    return (
        <div className="relative w-full">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -inset-[1px] rounded-[24px] bg-gradient-to-r from-cyan-500/50 via-blue-500/40 to-purple-500/35 blur-[0.5px]"
            />
            <div className="relative flex items-end gap-3 rounded-[24px] border border-slate-200 bg-white px-4 py-2 sm:py-2.5 shadow-sm">
                {/* <Search className="h-4 w-4 text-slate-400" /> */}
                <textarea
                    ref={textareaRef}
                    value={text}
                    onChange={(e) => {
                        const v = e.target.value;
                        e.target.style.height = "auto";
                        const nextHeight = Math.min(e.target.scrollHeight, maxHeightPx);
                        e.target.style.height = `${nextHeight}px`;
                        if (!isControlled) setInnerValue(v);
                        onChange?.(v);
                    }}
                    onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        if (e.shiftKey) return;
                        e.preventDefault();
                        if (!canSend) return;
                        onSend?.(text);
                        if (textareaRef.current) {
                            textareaRef.current.style.height = "auto";
                        }
                    }}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    rows={1}
                    className="w-full resize-none bg-transparent text-base sm:text-sm text-slate-700 placeholder:text-slate-400 outline-none min-h-[28px] max-h-[120px] overflow-y-auto py-1 pr-1 leading-tight"
                />
                <button
                    onClick={() => {
                        if (!canSend) return;
                        onSend?.(text);
                        if (textareaRef.current) {
                            textareaRef.current.style.height = "auto";
                        }
                    }}
                    disabled={!canSend}
                    className={
                        "mb-[2px] shrink-0 grid h-7 w-7 place-items-center rounded-full bg-gradient-to-r from-cyan-500/80 to-blue-500/80 shadow-[0_10px_25px_-18px_rgba(34,211,238,0.85)] " +
                        (canSend
                            ? "hover:from-cyan-400/90 hover:to-blue-400/90"
                            : "opacity-50")
                    }
                    aria-label="Analyze"
                    type="button"
                >
                    <Icon className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
